import {
  GetExamLocationRequestInfoDocument,
  useCreateExamRoomRequestInfoMutation,
  useUpdateExamRoomRequestInfoMutation,
} from '@exam-center/graphql';
import { useNavigate, useParams } from '@exam-center/router';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Form,
  FormButton,
  FormNumberInput,
  FormTextField,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';

import { ExamRoomCreateFormSchema, type ExamRoomCreateFormInput } from './schema';

interface ExamRoomData {
  floor: string;
  name: string;
  roomNumber?: string;
  seatCount: number | string;
}

type ExamRoomModalMode = 'create' | 'update';

interface ExamRoomCreateModalProps {
  open: boolean;
  onClose: () => void;
  roomRequestId?: string;
  locationRequestId: string;
  defaultValues?: ExamRoomData;
}

const DEFAULT_FORM_VALUES: ExamRoomCreateFormInput = {
  floor: '',
  name: '',
  roomNumber: '',
  seatCount: '' as unknown as number,
};

const MODAL_TEXT = {
  create: {
    title: 'เพิ่มข้อมูลห้องสอบ',
    button: 'เพิ่มห้องสอบ',
    successMessage: 'บันทึกข้อมูลห้องสอบเรียบร้อยแล้ว',
  },
  update: {
    title: 'แก้ไขข้อมูลห้องสอบ',
    button: 'บันทึกห้องสอบ',
    successMessage: 'บันทึกข้อมูลห้องสอบเรียบร้อยแล้ว',
  },
} as const;

const convertDefaultValues = (defaultValues?: ExamRoomData): ExamRoomCreateFormInput => {
  if (!defaultValues) return DEFAULT_FORM_VALUES;

  return {
    floor: defaultValues.floor,
    name: defaultValues.name,
    roomNumber: defaultValues.roomNumber || '',
    seatCount: !defaultValues.seatCount
      ? ('' as unknown as number)
      : Number(defaultValues.seatCount), // need to be number
  };
};

export default function ExamRoomCreateModal(props: Readonly<ExamRoomCreateModalProps>) {
  const { open: isOpen, onClose, roomRequestId, locationRequestId, defaultValues } = props;

  const { showSnackbar } = useSnackbar();

  const [updateExamRoomRequestInfo, { loading: isUpdating }] =
    useUpdateExamRoomRequestInfoMutation();
  const [createExamRoomRequestInfo, { loading: isCreating }] =
    useCreateExamRoomRequestInfoMutation();

  const modalMode: ExamRoomModalMode = roomRequestId ? 'update' : 'create';

  const formContext = useForm<ExamRoomCreateFormInput>({
    resolver: zodResolver(ExamRoomCreateFormSchema),
    defaultValues: convertDefaultValues(defaultValues),
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = formContext;

  const { examCenterCode } = useParams('/:examCenterCode/exam-location/requests/draft/:requestId');

  const navigate = useNavigate();

  const handleCreateOrUpdateExamRoom = async (data: ExamRoomCreateFormInput) => {
    const sharedMutationOptions = {
      onError: (error: Error) => {
        console.error('Error submitting form:', error);
        showSnackbar({
          message: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง',
          variant: 'error',
        });
      },
      refetchQueries: [GetExamLocationRequestInfoDocument],
    };

    if (roomRequestId) {
      await updateExamRoomRequestInfo({
        variables: {
          updateExamRoomRequestInfoInput: {
            id: roomRequestId,
            floor: data.floor,
            name: data.name,
            roomNumber: data.roomNumber?.trim() || null,
            seatCount: data.seatCount,
          },
        },
        ...sharedMutationOptions,
        onCompleted: () => {
          showSnackbar({
            message: MODAL_TEXT[modalMode].successMessage,
            variant: 'success',
          });
          onClose();
        },
      });
    } else {
      await createExamRoomRequestInfo({
        variables: {
          createExamRoomRequestInfoInput: {
            examLocationRequestInfoId: locationRequestId,
            floor: data.floor,
            name: data.name,
            roomNumber: data.roomNumber?.trim() || null,
            seatCount: data.seatCount,
          },
        },
        ...sharedMutationOptions,
        onCompleted: (res) => {
          if (res?.ecCreateExamRoomRequestInfo?.examRoomRequestInfo?.id) {
            navigate(
              '/:examCenterCode/exam-location/requests/draft/:requestId/room/:roomRequestId',
              {
                params: {
                  examCenterCode,
                  requestId: locationRequestId,
                  roomRequestId: res.ecCreateExamRoomRequestInfo.examRoomRequestInfo.id,
                },
              },
            );
          }
          showSnackbar({
            message: MODAL_TEXT[modalMode].successMessage,
            variant: 'success',
          });
          onClose();
        },
      });
    }
  };

  const handleResetForm = () => {
    reset(convertDefaultValues(defaultValues));
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth='sm'
        slotProps={{
          paper: {
            component: Form,
            onSubmit: handleSubmit(handleCreateOrUpdateExamRoom),
          },
          transition: {
            onExited: handleResetForm,
          },
        }}
      >
        <DialogTitle onClose={onClose} loading={isSubmitting}>
          {MODAL_TEXT[modalMode].title}
        </DialogTitle>
        <Divider />
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: (theme) => theme.spacing(4) }}
        >
          <FormTextField name='floor' label='ชั้น' placeholder='กรุณากรอกชั้นห้องสอบ' required />
          <FormTextField name='name' label='ชื่อห้อง' placeholder='กรุณากรอกชื่อห้องสอบ' required />
          <FormTextField
            name='roomNumber'
            label='เลขที่ห้อง'
            placeholder='กรุณากรอกเลขที่ห้องสอบ'
          />
          <FormNumberInput
            name='seatCount'
            label='จำนวนที่นั่ง'
            placeholder='กรุณากรอกจำนวนที่นั่ง'
            required
          />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            size='large'
            variant='outlined'
            color='secondary'
            onClick={onClose}
            disabled={isSubmitting}
          >
            ยกเลิก
          </Button>
          <FormButton
            label={MODAL_TEXT[modalMode].button}
            size='large'
            disabled={isSubmitting || isUpdating || isCreating}
          />
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
